var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { memo, useCallback, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import DomPurify from "isomorphic-dompurify";
import { __, translateBackendErrorCode } from "@jti/fe-common";
import { useOtPurposesByStep } from "@jti/rest-api";
import { Button, DEPRECATED_TextInput, FormField, Loading, MessageBox, MessageBoxVariant, PersonalDetails, Recaptchabar, RegistrationPolicyCheckFields, StreetAddressFields, Txt, useConfig, useFormContext, } from "@jti/ui";
import { useAemModel } from "../../../../contexts/AemModelContext";
import { useCustomAttributes } from "../../../../hooks/useCustomAttributes";
import { usePhoneService } from "../../../../hooks/usePhoneService";
import { useProfile } from "../../../../hooks/useProfile";
import styles from "./PersonalDetailsPage.module.scss";
const defaultTimerValue = 180;
const defaultAttemptsNo = 3;
const PersonalDetailsPage = ({ isOTPenabled = false, isSubmitting = false, onNext, fieldName = (name) => name, isLast, isAgeVerificationEnabled, }) => {
    const config = useConfig();
    const otpTimer = Number.parseInt(config.REGISTRATION_SMS_OTP_TIMER, 10);
    const phoneValidationCodeTimeout = Number.isNaN(otpTimer) ? defaultTimerValue : otpTimer;
    const phoneValidationCounterEnabled = config.REGISTRATION_SMS_OTP_COUNTER_ENABLED === "true";
    const attemptsNo = Number.parseInt(config.REGISTRATION_SMS_OTP_NUMBER_OF_ATTEMPTS, 10);
    const baseAttemptsNo = Number.isNaN(attemptsNo) ? defaultAttemptsNo : attemptsNo;
    const { verifyOtp, checkValidationStatus, initPhoneValidationByOtp } = usePhoneService();
    const { checkPhone } = useProfile();
    const { isLoadingCustomAttrs, orderedCustomAttributes } = useCustomAttributes();
    const { consents, genderInformation, registrationCommon, phoneValidationOtp } = useAemModel();
    const { avDisclaimerMessage = "", isRegistrationClosableBeforeAv = false, isAutocompleteDisabled = false, isPostCodeNotRequired = false, } = registrationCommon || {};
    const { data: purposes, isError: isOtError } = useOtPurposesByStep("registration", isLast);
    const [phoneVerificationUuid, setPhoneVerificationUuid] = useState();
    const [attemptLeft, setAttemptLeft] = useState(baseAttemptsNo);
    const { sanitize } = DomPurify;
    const poolingValidationRef = useRef();
    const [isPhoneValidatedViaSms, setIsPhoneValidatedViaSms] = useState(false);
    const { watch, setValue, setError, formState, clearErrors } = useFormContext();
    const telephone = watch(fieldName("telephone"));
    const phone_validation_code = watch(fieldName("phone_validation_code"));
    const phone_validation_uuid = watch(fieldName("phone_validation_uuid"));
    const isAgeVerificationDisclaimerVisible = isAgeVerificationEnabled && !!avDisclaimerMessage;
    const isRegistrationClosable = isAgeVerificationEnabled && isRegistrationClosableBeforeAv;
    const isDisabled = !formState.isDirty ||
        (isOTPenabled && !phone_validation_code && !isPhoneValidatedViaSms) ||
        Object.keys(formState.errors).length > 0;
    const stopValidationPolling = () => {
        clearInterval(poolingValidationRef.current);
    };
    const checkPhoneValidationStatus = useCallback((uuid, hash) => __awaiter(void 0, void 0, void 0, function* () {
        const { is_verified } = yield checkValidationStatus(uuid, hash);
        if (is_verified) {
            stopValidationPolling();
            setIsPhoneValidatedViaSms(true);
            setValue(fieldName("phone_validation_uuid"), uuid);
        }
    }), [checkValidationStatus, fieldName, setValue]);
    const startValidationPolling = useCallback((uuid, hash) => {
        poolingValidationRef.current = setInterval(() => checkPhoneValidationStatus(uuid, hash), 5000);
    }, [checkPhoneValidationStatus]);
    const sendCode = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        try {
            stopValidationPolling();
            clearErrors("phone_validation_code");
            setValue("phone_validation_code", "");
            const { uuid, hash } = yield initPhoneValidationByOtp(telephone);
            setAttemptLeft(baseAttemptsNo);
            setPhoneVerificationUuid(uuid);
            startValidationPolling(uuid, hash);
        }
        catch (error) {
            const errorMessage = translateBackendErrorCode(error === null || error === void 0 ? void 0 : error.error_code, __("auth.registration.login_credentials.sms_verify.init_validation_failed"));
            toast.error(errorMessage);
        }
    }), [clearErrors, initPhoneValidationByOtp, setValue, telephone]);
    const verifyOtpCheck = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (phone_validation_uuid) {
            stopValidationPolling();
            onNext();
            return;
        }
        if (phoneVerificationUuid === undefined) {
            return;
        }
        if (attemptLeft <= 0) {
            setError("phone_validation_code", {
                type: "manual",
                message: __("auth.registration.login_credentials.sms_verify.no_attempts_left"),
            });
            return;
        }
        try {
            const { uuid } = yield verifyOtp(phoneVerificationUuid, phone_validation_code);
            if (uuid) {
                yield setValue(fieldName("phone_validation_uuid"), uuid);
                stopValidationPolling();
                onNext();
            }
            else {
                throw new Error("Unexpected content from verifyOTP");
            }
        }
        catch (error) {
            const attemptNo = attemptLeft - 1;
            setAttemptLeft(attemptNo);
            const message = attemptNo > 0
                ? __("auth.registration.login_credentials.sms_verify.attempts_left", [attemptNo])
                : __("auth.registration.login_credentials.sms_verify.no_attempts_left");
            setError("phone_validation_code", {
                type: "manual",
                message,
            });
        }
    }), [
        phone_validation_uuid,
        onNext,
        verifyOtp,
        phoneVerificationUuid,
        phone_validation_code,
        setValue,
        fieldName,
        attemptLeft,
        setError,
    ]);
    const onSubmit = useCallback((event) => __awaiter(void 0, void 0, void 0, function* () {
        event.preventDefault();
        if (isOTPenabled && !isPhoneValidatedViaSms) {
            verifyOtpCheck();
        }
        else {
            onNext();
        }
    }), [isOTPenabled, isPhoneValidatedViaSms, verifyOtpCheck, onNext]);
    const nextButtonLabel = useMemo(() => {
        if (isLast) {
            return __("auth.registration.personal_details.btn_create");
        }
        if (isAgeVerificationEnabled) {
            return __("auth.registration.next_step.age_verification");
        }
        return __("auth.registration.next_step.device_registration");
    }, [isAgeVerificationEnabled, isLast]);
    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.isDefaultPrevented() && !isDisabled) {
            onSubmit(e);
        }
    };
    const updateCustomAttributesFields = (address) => __awaiter(void 0, void 0, void 0, function* () {
        if (address.postcode && !isPostCodeNotRequired) {
            yield setValue(fieldName("custom_attributes.post_code"), address.postcode, {
                shouldValidate: true,
            });
            clearErrors("custom_attributes.post_code");
        }
        if (address.streetNumber) {
            clearErrors(fieldName("streetNumber"));
        }
        if (address.city) {
            yield setValue(fieldName("custom_attributes.city"), address.city, {
                shouldValidate: true,
            });
        }
    });
    const renderAdditionalRegisterFields = () => {
        if (!isLoadingCustomAttrs) {
            return (orderedCustomAttributes || []).map((field, index) => {
                const required = field.is_required ? __("form_validation.field_required") : false;
                const pattern = field.validation ? new RegExp(field.validation) : new RegExp(".*");
                if (isPostCodeNotRequired && field.code === "post_code") {
                    return null;
                }
                if (field.type === "address") {
                    return (_jsx(StreetAddressFields, { required: field.is_required, enableStreetNameAutocomplete: !isAutocompleteDisabled, onUpdateStreet: updateCustomAttributesFields, locationServiceAutocompleteProvider: config.locationServiceAutocompleteProvider }, index));
                }
                return (_jsx("div", { className: styles.additionalFieldWrapper, children: _jsx(FormField, { labelId: `label-custom_attributes.${field.code}`, name: `custom_attributes.${field.code}`, label: __(`auth.account_custom_attribute.label.${field.code}`, [], field.label || ""), render: ({ field: controllerField }) => (_jsx(DEPRECATED_TextInput, Object.assign({}, controllerField, { "aria-labelledby": `label-custom_attributes.${field.code}`, placeholder: __(`auth.account_custom_attribute.hint.${field.code}`, [], field.hint || ""), dataTestId: `custom_attributes_field.${field.code}` }))), validation: {
                            required,
                            pattern: {
                                value: pattern,
                                message: __("auth.registration.pattern_error_message"),
                            },
                        } }, index) }, field.code));
            });
        }
        return null;
    };
    const renderForm = () => {
        if (isLoadingCustomAttrs) {
            return (_jsx("div", { "data-testid": "personalDetailsLoader", children: _jsx(Loading, {}) }));
        }
        return (_jsxs(_Fragment, { children: [_jsxs("div", { className: styles.wrap, children: [_jsx(PersonalDetails, { sendCode: sendCode, onCheckPhone: checkPhone, isOTPenabled: isOTPenabled, enableCounter: phoneValidationCounterEnabled, timer: phoneValidationCodeTimeout, attemptsNo: baseAttemptsNo, onSubmitOTP: verifyOtpCheck, isGenderOptionEnabled: genderInformation === null || genderInformation === void 0 ? void 0 : genderInformation.isGenderEnabled, isPhoneValidatedViaSms: isPhoneValidatedViaSms, isOtpNumeric: phoneValidationOtp === null || phoneValidationOtp === void 0 ? void 0 : phoneValidationOtp.isOtpNumeric, isSingleNameField: config.isSingleNameField }), _jsx("div", { className: styles.additionalFieldsContainer, children: renderAdditionalRegisterFields() }), _jsx("div", { className: styles.checkboxGroup, children: isLast && (_jsx(RegistrationPolicyCheckFields, { policyFields: purposes, fieldName: (name) => `purposes.${name}`, isError: isOtError, oneTrustErrorLinkText: consents === null || consents === void 0 ? void 0 : consents.oneTrustErrorLinkText, oneTrustErrorMessage: consents === null || consents === void 0 ? void 0 : consents.oneTrustErrorMessage, oneTrustErrorRedirectUrl: consents === null || consents === void 0 ? void 0 : consents.oneTrustErrorRedirectUrl })) })] }), _jsxs("div", { className: styles.formFooter, children: [_jsx(Txt, { className: styles.required, children: __("profile.password.required_field") }), _jsxs("div", { className: styles.buttonsWrapper, children: [_jsxs("div", { className: styles.buttons, children: [_jsx(Button, { "data-testid": "personalDetailsBtn", onClick: onSubmit, disabled: isDisabled || isOtError, isPending: isSubmitting, children: nextButtonLabel }), isRegistrationClosable && (_jsx(Button, { href: config.HOME_PAGE_URL, "data-testid": "cancelRegistration", color: "secondary", className: styles.action, nativeBehaviour: true, children: __("auth.registration.personal_details.close_registration") }))] }), isLast && config.reCaptchaEnabled && config.brandingInformationEnabled && _jsx(Recaptchabar, {})] }), _jsx(MessageBox, { className: styles.avDisclaimer, isVisible: isAgeVerificationDisclaimerVisible, variant: MessageBoxVariant.warning, hideIcon: true, isScrollable: false, "data-testid": "avLegalDisclaimer", children: _jsx("span", { dangerouslySetInnerHTML: { __html: sanitize(avDisclaimerMessage, { ADD_ATTR: ["target"] }) } }) })] })] }));
    };
    return (_jsxs("div", { onKeyDown: handleKeyDown, className: styles.container, children: [_jsx("div", { className: styles.wrap, children: _jsx("h2", { className: styles.header, children: __("auth.registration.personal_details.title_step2") }, "personal_details") }), renderForm()] }));
};
export default memo(PersonalDetailsPage);
